import { Fragment, useMemo, useState } from "react";
import { ArrowLeft, Plus, Gift, Plane, Edit2, Trash2, SlidersHorizontal } from "lucide-react";
import Button from "../ui/Button";
import Pill from "../ui/Pill";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import ConfirmDialog from "../ui/ConfirmDialog";
import TeamForm from "./TeamForm";
import BonusForm from "./BonusForm";
import VacationForm from "./VacationForm";
import PartnerRulesEditor from "./PartnerRulesEditor";
import { formatUSD, formatMonths } from "../../lib/formatters";
import { monthsBetween, today } from "../../lib/dateUtils";
import {
  computePartnerOwedForMonth, computePartnerPaidForMonth,
  resolveTeamMonthSalary, resolveTeamMonthBonus,
} from "../../lib/calculations";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Mirror of calculations.costFromAllocations' per-row amount math so we can
// render a single allocation's $ contribution on the profile without
// re-running the whole P&L aggregator.
function allocAmountFromBase(alloc, base) {
  const v = Number(alloc.value) || 0;
  return alloc.mode === "amount" ? v : (Number(base) || 0) * (v / 100);
}

function allocScope(alloc) {
  return alloc.scope || (alloc.offerId ? "offer" : null);
}

function allocTargetLabel(alloc, offers) {
  const scope = allocScope(alloc);
  if (scope === "offer") {
    const o = offers.find((x) => x.id === alloc.offerId);
    return o ? o.name : "(deleted offer)";
  }
  if (scope === "department") return alloc.scopeId || "(no department)";
  if (scope === "agency") return "All offers (agency overhead)";
  return "Unassigned";
}

function allocPnlHint(alloc, offers) {
  const scope = allocScope(alloc);
  const ct = (alloc.costType || "cogs").toUpperCase();
  if (scope === "offer") {
    const o = offers.find((x) => x.id === alloc.offerId);
    const dept = o?.department ? ` (rolls into ${o.department} dept + Agency P&L)` : " (rolls into Agency P&L)";
    return `Hits ${ct} on offer "${o?.name || "?"}"${dept}`;
  }
  if (scope === "department") {
    return `Hits ${ct} on ${alloc.scopeId || "?"} department P&L (and Agency P&L)`;
  }
  if (scope === "agency") {
    return `Hits ${ct} on Agency P&L only — visible on Overview. Does NOT drag any department's margins.`;
  }
  return "No scope set — cost is unattributed and won't show in any P&L.";
}

// How many days in a vacation range fall inside a specific YYYY-MM month.
function vacationDaysInMonth(vacations, year, monthIdx) {
  const monthStart = new Date(year, monthIdx, 1);
  const monthEnd = new Date(year, monthIdx + 1, 0);
  let days = 0;
  for (const v of vacations || []) {
    if (!v.startDate || !v.endDate) continue;
    const start = new Date(v.startDate + "T00:00:00");
    const end = new Date(v.endDate + "T00:00:00");
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    const lo = start > monthStart ? start : monthStart;
    const hi = end < monthEnd ? end : monthEnd;
    if (hi < lo) continue;
    days += Math.round((hi - lo) / (1000 * 60 * 60 * 24)) + 1;
  }
  return days;
}

// Salary deduction for vacation days in a given month, prorated by the
// actual number of days in the month. Matches VacationForm.computeDeduction.
function vacationDeduction(monthlySalary, year, monthIdx, vacationDays) {
  if (!vacationDays || !monthlySalary) return 0;
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  return (Number(monthlySalary) || 0) * (vacationDays / daysInMonth);
}

// Returns a Set of "YYYY-MM-DD" strings covering every day in the given
// vacation rows (inclusive endpoints).
function expandVacationDays(vacations) {
  const out = new Set();
  for (const v of vacations || []) {
    if (!v.startDate || !v.endDate) continue;
    const start = new Date(v.startDate + "T00:00:00");
    const end = new Date(v.endDate + "T00:00:00");
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    const cur = new Date(start);
    while (cur <= end) {
      out.add(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
  }
  return out;
}

export default function TeamProfile({ member, state, dashboard, dateFilter, onBack }) {
  const [editOpen, setEditOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [vacationOpen, setVacationOpen] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const allVacations = state.vacations || [];
  const memberVacations = useMemo(
    () => allVacations.filter((v) => v.memberId === member.id).sort((a, b) => (b.startDate || "").localeCompare(a.startDate || "")),
    [allVacations, member.id]
  );
  const vacationDates = useMemo(() => expandVacationDays(memberVacations), [memberVacations]);

  const currentYear = new Date().getFullYear();
  const year = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : currentYear;

  // Filter vacations to the active year for the day count + calendar grid.
  const yearVacationDays = useMemo(() => {
    let count = 0;
    for (const iso of vacationDates) {
      if (iso.startsWith(`${year}-`)) count++;
    }
    return count;
  }, [vacationDates, year]);

  const bonusByMonth = member.bonusByMonth || {};
  const yearBonusTotal = useMemo(() => {
    let sum = 0;
    for (const [mKey, amt] of Object.entries(bonusByMonth)) {
      if (mKey.startsWith(`${year}-`)) sum += Number(amt) || 0;
    }
    return sum;
  }, [bonusByMonth, year]);

  const monthlySalary = Number(member.monthlySalary) || 0;
  const tenureMonths = monthsBetween(member.startDate, member.endDate || new Date().toISOString().slice(0, 10));

  // Stable synthetic initial for VacationForm when not editing — avoids
  // recreating an object literal on every render (which would churn the
  // form's effect deps).
  const newVacationInitial = useMemo(
    () => ({ memberId: member.id, startDate: "", endDate: "", notes: "" }),
    [member.id]
  );
  const singleMemberTeam = useMemo(() => [member], [member]);

  // (statusOptions intentionally not passed — TeamForm uses its own list.)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate">
            <ArrowLeft size={16} />
          </button>
          <div className="w-9 h-9 rounded-full bg-brand-ink text-white font-display font-semibold flex items-center justify-center text-sm">
            {(member.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div>
            <h2 className="font-display font-semibold text-xl text-brand-ink dark:text-[var(--text-primary)]">{member.name}</h2>
            <div className="text-xs text-brand-slate flex items-center gap-2">
              <span>{member.role || "—"}</span>
              {member.department && <span>· {member.department}</span>}
              {member.email && <span>· {member.email}</span>}
              <Pill status={member.status || "active"}>{member.status || "active"}</Pill>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setEditOpen(true)}>
            <Edit2 size={13} /> Edit
          </Button>
          <Button
            variant="danger"
            onClick={() => setConfirm({
              title: `Delete ${member.name}?`,
              message: `Permanently delete this ${member.type === "contractor" ? "contractor" : "team member"}. Their vacations, bonuses, partner rules and matched expenses will lose their link to this record (data is not removed, just unlinked). This cannot be undone.`,
              confirmLabel: "Delete",
              onConfirm: () => {
                dashboard.deleteTeamMember(member.id);
                setConfirm(null);
                onBack?.();
              },
            })}
          >
            <Trash2 size={13} /> Delete
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <Kpi label="Monthly salary" value={formatUSD(monthlySalary)} />
        <Kpi label={`Bonuses ${year}`} value={formatUSD(yearBonusTotal)} />
        <Kpi label={`Vacation days ${year}`} value={String(yearVacationDays)} />
        <Kpi label="Tenure" value={formatMonths(tenureMonths)} />
      </div>

      {/* Payroll matrix — months as columns, salary + bonus per month, with
          a Plane icon on months with vacation days and a Gift icon on
          months with a bonus. */}
      <PayrollMatrix
        member={member}
        year={year}
        vacations={memberVacations}
        fxRates={state.fxRates || []}
        onAddBonus={() => setBonusOpen(true)}
        onAddVacation={() => { setEditingVacation(null); setVacationOpen(true); }}
      />

      {/* Allocations: where this member's salary lands in the P&L */}
      <AllocationsSection
        member={member}
        state={state}
        year={year}
        vacations={memberVacations}
        onEdit={() => setEditOpen(true)}
      />

      {/* Partner rules + cut owed/paid YTD. Only mounts a meaningful summary
          if this member has at least one partner rule. */}
      <PartnerSection member={member} state={state} dashboard={dashboard} year={year} />

      {/* Bonus log — short table for edit/delete */}
      {Object.entries(bonusByMonth).filter(([k, v]) => k.startsWith(`${year}-`) && Number(v) > 0).length > 0 && (
        <Section
          title={`Bonus log · ${year}`}
          icon={<Gift size={14} className="text-amber-600" />}
        >
          <Table>
            <THead>
              <TR>
                <TH>Month</TH>
                <TH className="text-right">Amount</TH>
                <TH></TH>
              </TR>
            </THead>
            <tbody>
              {Object.entries(bonusByMonth)
                .filter(([k, v]) => k.startsWith(`${year}-`) && Number(v) > 0)
                .sort(([a], [b]) => b.localeCompare(a))
                .map(([mKey, amount]) => {
                  const monthIdx = Number(mKey.slice(5, 7)) - 1;
                  return (
                    <TR key={mKey}>
                      <TD className="text-xs">{MONTH_LABELS[monthIdx]} {year}</TD>
                      <TD className="text-right text-xs font-medium">{formatUSD(Number(amount))}</TD>
                      <TD className="text-right whitespace-nowrap">
                        <button
                          title="Edit (re-open Add bonus and pick this month to change the value)"
                          onClick={() => setBonusOpen(true)}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate hover:text-brand-ink"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          title="Delete bonus"
                          onClick={() => setConfirm({
                            title: "Delete bonus",
                            message: `Remove the ${formatUSD(Number(amount))} bonus for ${MONTH_LABELS[monthIdx]} ${year}?`,
                            confirmLabel: "Delete",
                            onConfirm: () => {
                              dashboard.updateTeamCell(member.id, mKey, "bonus", "");
                              setConfirm(null);
                            },
                          })}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate hover:text-red-600"
                        >
                          <Trash2 size={13} />
                        </button>
                      </TD>
                    </TR>
                  );
                })}
            </tbody>
          </Table>
        </Section>
      )}

      {/* Vacation log — short table so the user can edit/delete entries */}
      {memberVacations.filter((v) => v.startDate?.startsWith(`${year}-`) || v.endDate?.startsWith(`${year}-`)).length > 0 && (
        <Section
          title={`Vacation log · ${year}`}
          icon={<Plane size={14} className="text-emerald-600" />}
        >
          <Table>
            <THead>
              <TR>
                <TH>Start</TH>
                <TH>End</TH>
                <TH>Days</TH>
                <TH>Notes</TH>
                <TH></TH>
              </TR>
            </THead>
            <tbody>
              {memberVacations
                .filter((v) => v.startDate?.startsWith(`${year}-`) || v.endDate?.startsWith(`${year}-`))
                .map((v) => {
                  const days = countDays(v.startDate, v.endDate);
                  return (
                    <TR key={v.id}>
                      <TD className="text-xs">{v.startDate}</TD>
                      <TD className="text-xs">{v.endDate}</TD>
                      <TD className="text-xs">{days}</TD>
                      <TD className="text-xs text-brand-slate">{v.notes || "—"}</TD>
                      <TD className="text-right whitespace-nowrap">
                        <button
                          title="Edit"
                          onClick={() => { setEditingVacation(v); setVacationOpen(true); }}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate hover:text-brand-ink"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => setConfirm({
                            title: "Delete vacation",
                            message: `Delete vacation ${v.startDate} → ${v.endDate}?`,
                            confirmLabel: "Delete",
                            onConfirm: () => { dashboard.deleteVacation(v.id); setConfirm(null); },
                          })}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate hover:text-red-600"
                        >
                          <Trash2 size={13} />
                        </button>
                      </TD>
                    </TR>
                  );
                })}
            </tbody>
          </Table>
        </Section>
      )}

      {/* Notes */}
      {member.notes && (
        <Section title="Notes">
          <div className="text-sm text-brand-ink dark:text-[var(--text-primary)] whitespace-pre-wrap p-3">{member.notes}</div>
        </Section>
      )}

      {/* Modals — only mounted when open, so their internal effects don't
          churn while the profile is just sitting on the page. */}
      {editOpen && (
        <TeamForm
          open={true}
          onClose={() => setEditOpen(false)}
          onSubmit={(data) => dashboard.updateTeamMember(member.id, data)}
          initial={member}
          offers={state.offers || []}
          fxRates={state.fxRates || []}
          departments={state.settings?.departments || []}
        />
      )}
      {bonusOpen && (
        <BonusForm
          open={true}
          onClose={() => setBonusOpen(false)}
          team={singleMemberTeam}
          fxRates={state.fxRates || []}
          onSave={(memberId, monthKey, usdAmount) =>
            dashboard.updateTeamCell(memberId, monthKey, "bonus", usdAmount)
          }
        />
      )}
      {vacationOpen && (
        <VacationForm
          open={true}
          onClose={() => { setVacationOpen(false); setEditingVacation(null); }}
          onSubmit={(data) => {
            if (editingVacation) dashboard.updateVacation(editingVacation.id, data);
            else dashboard.addVacation({ ...data, memberId: member.id });
          }}
          initial={editingVacation || newVacationInitial}
          team={singleMemberTeam}
        />
      )}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={confirm?.onConfirm || (() => {})}
      />
    </div>
  );
}

// Tools-style 12-month matrix for a single employee. Rows: Salary, Bonus,
// Vacation days (with Plane icon). Bonus row carries the Gift icon. Each
// cell shows the relevant value or "—".
function PayrollMatrix({ member, year, vacations, fxRates = [], onAddBonus, onAddVacation }) {
  const bonusByMonth = member.bonusByMonth || {};
  const currentYear = new Date().getFullYear();
  const currentMonthIdx = new Date().getMonth();

  // Salary row uses resolveTeamMonthSalary so partial first/last months
  // (e.g. starts Jan 19, ends Apr 27) prorate by days. Vacation days are
  // already deducted inside resolveTeamMonthSalary via teamMonthFraction,
  // so we display them as informational but don't subtract again.
  const salaryRow = MONTH_LABELS.map((_, i) => {
    const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
    return resolveTeamMonthSalary(member, mKey, vacations, fxRates);
  });
  const bonusRow = MONTH_LABELS.map((_, i) => Number(bonusByMonth[`${year}-${String(i + 1).padStart(2, "0")}`] || 0));
  const vacationDaysRow = MONTH_LABELS.map((_, i) => vacationDaysInMonth(vacations, year, i));
  // vacationDeductionRow is kept for display only — the salary already
  // reflects the deduction. Show what the days *would* cost so Ahmed
  // sees the impact, but don't double-subtract from the monthly total.
  const vacationDeductionRow = MONTH_LABELS.map((_, i) => {
    // Reconstruct the "before vacation" salary to compute the deduction,
    // strictly for display in the Vacation row.
    const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
    const fullForMonth = resolveTeamMonthSalary(member, mKey, [], fxRates); // ignore vacations
    return Math.max(0, fullForMonth - salaryRow[i]);
  });
  // Monthly cost = prorated salary (vacation already deducted) + bonus
  const totalRow = MONTH_LABELS.map((_, i) => salaryRow[i] + bonusRow[i]);

  const salaryYear = salaryRow.reduce((a, b) => a + b, 0);
  const bonusYear = bonusRow.reduce((a, b) => a + b, 0);
  const vacationDaysYear = vacationDaysRow.reduce((a, b) => a + b, 0);
  const vacationDeductionYear = vacationDeductionRow.reduce((a, b) => a + b, 0);
  const totalYear = salaryYear + bonusYear;

  return (
    <div className="mb-5 bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">
            Payroll · {year}
          </div>
          <div className="text-xs text-brand-slate mt-0.5">
            Salary + bonus per month. Plane = vacation taken that month, Gift = bonus that month.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onAddBonus}>
            <Gift size={12} /> Add bonus
          </Button>
          <Button variant="secondary" size="sm" onClick={onAddVacation}>
            <Plane size={12} /> Add vacation
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col style={{ width: "16%" }} />
            {MONTH_LABELS.map((_, i) => <col key={i} style={{ width: `${76 / 12}%` }} />)}
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">Type</th>
              {MONTH_LABELS.map((m, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <th key={m} className={`px-1 py-2 text-center font-medium uppercase tracking-wide text-[10px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : "text-brand-slate"}`}>
                    {m}
                  </th>
                );
              })}
              <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px]">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-100 dark:border-[var(--border-soft)]">
              <td className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">Salary</td>
              {salaryRow.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <td key={i} className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${amount > 0 ? "text-brand-ink dark:text-[var(--text-primary)]" : "text-gray-300"}`}>
                    {amount > 0 ? formatUSD(amount, { decimals: 0 }) : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-semibold border-l border-gray-100 dark:border-[var(--border-soft)]">{formatUSD(salaryYear, { decimals: 0 })}</td>
            </tr>
            <tr className="border-t border-gray-100 dark:border-[var(--border-soft)] bg-amber-50/30 dark:bg-amber-900/5">
              <td className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">
                <span className="inline-flex items-center gap-1.5"><Gift size={12} className="text-amber-600" /> Bonus</span>
              </td>
              {bonusRow.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <td key={i} className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${amount > 0 ? "text-amber-800 font-medium" : "text-gray-300"}`}>
                    {amount > 0 ? formatUSD(amount, { decimals: 0 }) : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-semibold border-l border-gray-100 dark:border-[var(--border-soft)]">{bonusYear > 0 ? formatUSD(bonusYear, { decimals: 0 }) : "—"}</td>
            </tr>
            <tr className="border-t border-gray-100 dark:border-[var(--border-soft)] bg-emerald-50/30 dark:bg-emerald-900/5">
              <td className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">
                <span className="inline-flex items-center gap-1.5"><Plane size={12} className="text-emerald-600" /> Vacation</span>
              </td>
              {vacationDaysRow.map((days, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                const deduction = vacationDeductionRow[i];
                return (
                  <td key={i} className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${days > 0 ? "text-emerald-800 font-medium" : "text-gray-300"}`} title={days > 0 ? `${days} days · −${formatUSD(deduction, { decimals: 0 })} deduction` : undefined}>
                    {days > 0 ? (
                      <span className="inline-flex flex-col leading-tight">
                        <span>{days}d</span>
                        <span className="text-[10px] text-emerald-700 font-normal">−{formatUSD(deduction, { decimals: 0 })}</span>
                      </span>
                    ) : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-semibold border-l border-gray-100 dark:border-[var(--border-soft)]">
                {vacationDaysYear > 0 ? (
                  <span className="inline-flex flex-col leading-tight items-end">
                    <span>{vacationDaysYear}d</span>
                    <span className="text-[10px] text-emerald-700 font-normal">−{formatUSD(vacationDeductionYear, { decimals: 0 })}</span>
                  </span>
                ) : "—"}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-[var(--border-soft)] bg-brand-ink text-white">
              <td className="px-3 py-2 font-bold border-r border-white/10">Monthly cost</td>
              {totalRow.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <td key={i} className={`px-1 py-2 text-center font-bold ${isCurrent ? "text-brand-mint" : "text-white"}`}>
                    {amount > 0 ? formatUSD(amount, { decimals: 0 }) : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-bold text-white border-l border-white/10">{formatUSD(totalYear, { decimals: 0 })}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Partner cut summary + rules editor for the given member. Computes YTD
// "owed" from partner_rules and YTD "paid" from one_off_payments tagged as
// partner_payout. The outstanding line is owed - paid.
function PartnerSection({ member, state, dashboard, year }) {
  const partnerRules = state.partnerRules || [];
  const hasRules = partnerRules.some((r) => r.partnerId === member.id);

  const { owedYtd, paidYtd } = useMemo(() => {
    if (!hasRules) return { owedYtd: 0, paidYtd: 0 };
    const t = new Date();
    const currentMonthIdx = year === t.getFullYear() ? t.getMonth() : 11;
    let owed = 0;
    let paid = 0;
    for (let i = 0; i <= currentMonthIdx; i++) {
      const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
      owed += computePartnerOwedForMonth(member.id, mKey, state).owed;
      paid += computePartnerPaidForMonth(member.id, mKey, state);
    }
    return { owedYtd: owed, paidYtd: paid };
  }, [hasRules, member.id, state, year]);

  const outstanding = owedYtd - paidYtd;

  return (
    <div className="mb-5">
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] p-4 space-y-4">
        {hasRules && (
          <div className="grid grid-cols-3 gap-3">
            <Kpi label={`Cut owed ${year}`} value={formatUSD(owedYtd)} />
            <Kpi label={`Cut paid ${year}`} value={formatUSD(paidYtd)} />
            <Kpi
              label="Outstanding"
              value={formatUSD(Math.abs(outstanding))}
            />
          </div>
        )}
        <PartnerRulesEditor member={member} state={state} dashboard={dashboard} />
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-brand-slate font-medium">{label}</div>
      <div className="font-display font-semibold text-lg text-brand-ink dark:text-[var(--text-primary)] mt-0.5">{value}</div>
    </div>
  );
}

function Section({ title, icon, action, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-display font-semibold text-brand-ink dark:text-[var(--text-primary)]">{title}</h3>
        </div>
        {action}
      </div>
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] overflow-hidden">
        {children}
      </div>
    </div>
  );
}

// Allocations card: shows each allocation row's $ contribution this month
// and YTD, plus a hint of which P&L view it surfaces on. Mirrors the
// version on ToolProfile so behavior is consistent between the two
// allocation-driven entities.
function AllocationsSection({ member, state, year, vacations, onEdit }) {
  const allocations = Array.isArray(member.offerAllocations) ? member.offerAllocations : [];
  const offers = state.offers || [];
  const fxRates = state.fxRates || [];
  const currentMonthIdx = today().getMonth();
  const currentMonthKey = `${year}-${String(currentMonthIdx + 1).padStart(2, "0")}`;
  const currentYear = today().getFullYear();
  const cutoffMonthIdx = year === currentYear ? currentMonthIdx : 11;

  const memberMonthBase = (mKey) =>
    resolveTeamMonthSalary(member, mKey, vacations, fxRates) +
    resolveTeamMonthBonus(member, mKey);

  const allocRows = useMemo(() => {
    const currentBase = memberMonthBase(currentMonthKey);
    return allocations.map((a) => {
      let ytdAmt = 0;
      for (let i = 0; i <= cutoffMonthIdx; i++) {
        const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
        const base = memberMonthBase(mKey);
        if (base <= 0) continue;
        ytdAmt += allocAmountFromBase(a, base);
      }
      return {
        alloc: a,
        thisMonth: currentBase > 0 ? allocAmountFromBase(a, currentBase) : 0,
        ytd: ytdAmt,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocations, member, vacations, fxRates, year, currentMonthKey, cutoffMonthIdx]);

  const allocTotalThisMonth = allocRows.reduce((s, r) => s + r.thisMonth, 0);
  const allocTotalYtd = allocRows.reduce((s, r) => s + r.ytd, 0);
  const currentMonthSalary = memberMonthBase(currentMonthKey);
  const unattributedThisMonth = Math.max(0, currentMonthSalary - allocTotalThisMonth);

  return (
    <div className="mb-5 bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} className="text-brand-slate" />
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">
            Allocations · where this salary lands in P&amp;L
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={onEdit}>
          <Edit2 size={12} /> Edit allocations
        </Button>
      </div>
      {allocations.length === 0 ? (
        <div className="p-6">
          <EmptyState
            icon={SlidersHorizontal}
            title="No allocations set"
            message="This salary isn't being attributed to any P&L. Click Edit allocations to route it to an Offer, Department, or Agency overhead."
          />
        </div>
      ) : (
        <>
          <Table>
            <THead>
              <TR>
                <TH>Scope</TH>
                <TH>Target</TH>
                <TH>Rule</TH>
                <TH>Type</TH>
                <TH className="text-right">This month</TH>
                <TH className="text-right">YTD</TH>
              </TR>
            </THead>
            <tbody>
              {allocRows.map(({ alloc, thisMonth, ytd }, idx) => {
                const scope = allocScope(alloc);
                const ct = (alloc.costType || "cogs");
                const scopeStyle =
                  scope === "agency"
                    ? "bg-purple-50 text-purple-700 border-purple-200"
                    : scope === "department"
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-gray-50 text-gray-700 border-gray-200";
                const ctStyle =
                  ct === "cogs"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200";
                return (
                  <Fragment key={idx}>
                    <TR>
                      <TD>
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${scopeStyle}`}>
                          {scope || "—"}
                        </span>
                      </TD>
                      <TD className="font-medium">{allocTargetLabel(alloc, offers)}</TD>
                      <TD className="text-xs text-brand-slate">
                        {alloc.mode === "amount"
                          ? `$${(Number(alloc.value) || 0).toLocaleString()} fixed`
                          : `${Number(alloc.value) || 0}% of salary`}
                      </TD>
                      <TD>
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${ctStyle}`}>
                          {ct}
                        </span>
                      </TD>
                      <TD className="text-right font-medium">{formatUSD(thisMonth)}</TD>
                      <TD className="text-right font-medium">{formatUSD(ytd)}</TD>
                    </TR>
                    <TR className="bg-gray-50/60 dark:bg-[var(--bg-soft)]/40 border-t-0">
                      <TD colSpan={6} className="text-[11px] text-brand-slate italic pt-0">
                        → {allocPnlHint(alloc, offers)}
                      </TD>
                    </TR>
                  </Fragment>
                );
              })}
              <TR className="bg-gray-100 dark:bg-[var(--bg-soft)]/80 border-t-2 border-gray-200 dark:border-[var(--border-soft)]">
                <TD colSpan={4} className="font-semibold uppercase tracking-wide text-[10px] text-brand-slate">
                  Allocated total
                </TD>
                <TD className="text-right font-bold">{formatUSD(allocTotalThisMonth)}</TD>
                <TD className="text-right font-bold">{formatUSD(allocTotalYtd)}</TD>
              </TR>
              {unattributedThisMonth > 0.01 && (
                <TR>
                  <TD colSpan={4} className="text-[11px] text-red-600 font-medium">
                    Unattributed this month (salary not hitting any P&amp;L)
                  </TD>
                  <TD className="text-right font-medium text-red-600">{formatUSD(unattributedThisMonth)}</TD>
                  <TD />
                </TR>
              )}
            </tbody>
          </Table>
          <p className="px-4 py-3 text-[11px] text-brand-slate leading-relaxed">
            Percent allocations are applied to this month's salary + bonus (after vacation deduction). Fixed-amount allocations apply regardless of salary.
            Salary lands on Agency, Department, or Offer P&amp;L based on each row's scope — Agency-scope rows only show on the <strong>Overview</strong> tab.
          </p>
        </>
      )}
    </div>
  );
}

function countDays(startISO, endISO) {
  if (!startISO || !endISO) return 0;
  const a = new Date(startISO + "T00:00:00");
  const b = new Date(endISO + "T00:00:00");
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1);
}
